/**
 * Canny → Supercomputer generation: shared running→completed apply logic.
 *
 * Extracted out of `useGenerationPoller.ts` so both the poller (which polls
 * `GET /api/ai/wavespeed/status/<jobId>` every ~3s) and the upcoming SSE
 * push path can apply a completed job through one implementation, with one
 * idempotency guard. Callers only need the `jobId` (== WaveSpeed taskId ==
 * `job.jobId`); the proposal + job are looked up here.
 */
import { useEditorStore } from '@/lib/editor/editorStore';
import { isPollableGenerationJob } from '@/lib/editor/agent/genJobPollable';
import type { GenShotJob } from '@/types/editor';

/**
 * Applies a completed generation to the store exactly once (running →
 * completed). Safe to call more than once with the same `jobId`: the second
 * call finds the job no longer `isPollableGenerationJob` (already
 * `completed`) and is a no-op. All reads/writes here are synchronous — no
 * await between the guard and the mutations — so overlapping callers can't
 * interleave.
 */
export function applyCompletedGenJob(
  jobId: string,
  output: { src?: string; videoId?: string },
  aspectApplied: Set<string>
): void {
  // Re-read the freshest state right before mutating: guards against
  // double-add if a previous call (poller tick or SSE push) already flipped
  // this job to `completed` — only act on the running -> completed
  // transition.
  let found: { toolCallId: string; job: GenShotJob } | null = null;
  for (const proposal of useEditorStore.getState().genProposals) {
    const job = proposal.jobs.find((j) => j.jobId === jobId && isPollableGenerationJob(j));
    if (job) {
      found = { toolCallId: proposal.toolCallId, job };
      break;
    }
  }
  if (!found) return;
  const { toolCallId, job } = found;

  const src = output.src;
  if (!src) {
    useEditorStore.getState().updateGenJob(toolCallId, job.localId, {
      state: 'error',
      error: 'Generation completed with no output',
    });
    return;
  }

  useEditorStore.getState().addClip({
    id: jobId,
    videoId: output.videoId ?? undefined,
    title: job.shot.description,
    src,
    source: 'studio',
    duration: job.shot.durationSec,
  });

  if (!aspectApplied.has(toolCallId) && job.shot.order === 0 && job.shot.aspectRatio) {
    useEditorStore.getState().setAspectRatio(job.shot.aspectRatio, true);
    aspectApplied.add(toolCallId);
  }

  useEditorStore.getState().updateGenJob(toolCallId, job.localId, {
    state: 'completed',
    src,
  });
}

/**
 * Mirrors the `failed` branch: flips a still-pollable running job to
 * `error`. No-op if the job is no longer pollable (already terminal).
 */
export function failCompletedGenJob(jobId: string, error: string): void {
  for (const proposal of useEditorStore.getState().genProposals) {
    const job = proposal.jobs.find((j) => j.jobId === jobId && isPollableGenerationJob(j));
    if (job) {
      useEditorStore.getState().updateGenJob(proposal.toolCallId, job.localId, {
        state: 'error',
        error,
      });
      return;
    }
  }
}
